import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { BrainCircuit, Landmark, Code2, Cloud, Workflow, Target } from "lucide-react";
import { SectionHeading, FadeUp } from "./Reveal";

const PROOFS = [
  { title: "AI-native thinking", Icon: BrainCircuit },
  { title: "Enterprise architecture experience", Icon: Landmark },
  { title: "Full-stack engineering", Icon: Code2 },
  { title: "Cloud-first delivery", Icon: Cloud },
  { title: "Automation-first operations", Icon: Workflow },
  { title: "Business-outcome focus", Icon: Target },
];

const STATS = [
  { value: 13, suffix: "", label: "Industries served" },
  { value: 99.9, suffix: "%", label: "Reliability targets", decimals: 1 },
  { value: 24, suffix: "/7", label: "Operations coverage" },
  { value: 4, suffix: "", label: "Connected capabilities" },
];

const Counter = ({ value, suffix, decimals = 0, label }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-15%" });
  const [display, setDisplay] = useState("0");

  useEffect(() => {
    if (!inView) return;
    const start = performance.now();
    const dur = 1600;
    const tick = (now) => {
      const p = Math.min((now - start) / dur, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay((value * eased).toFixed(decimals));
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [inView, value, decimals]);

  return (
    <div ref={ref} className="text-center">
      <p className="font-display font-extrabold text-4xl sm:text-5xl tracking-tighter text-white">
        {display}<span className="jx-gradient-text">{suffix}</span>
      </p>
      <p className="mt-2 text-xs font-bold tracking-[0.2em] uppercase text-white/45">{label}</p>
    </div>
  );
};

export const WhyJiyonox = () => (
  <section id="about" data-testid="why-jiyonox-section" className="py-24 sm:py-32 bg-jx-night text-white relative overflow-hidden">
    <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[700px] h-[400px] rounded-full bg-jx-magenta/12 blur-[140px]" />
    <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
      <SectionHeading
        dark
        testId="why-jiyonox"
        overline="Why JIYONOX"
        title={<>Engineering depth. <span className="jx-gradient-text">Business outcomes.</span></>}
      />

      <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/8 rounded-3xl overflow-hidden border border-white/8">
        {PROOFS.map((p, i) => (
          <motion.div
            key={p.title}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.08, duration: 0.6 }}
            data-testid={`proof-point-${i}`}
            className="group bg-jx-night p-8 sm:p-10 hover:bg-white/[0.04] transition-colors duration-400"
          >
            <p.Icon className="w-6 h-6 text-jx-copper transition-transform duration-300 group-hover:scale-110 group-hover:-rotate-6" strokeWidth={1.6} />
            <p className="mt-5 font-display font-bold text-lg tracking-tight text-white">{p.title}</p>
          </motion.div>
        ))}
      </div>

      <FadeUp delay={0.2} className="mt-16">
        <p data-testid="why-statement" className="max-w-3xl mx-auto text-center font-display font-bold text-2xl sm:text-3xl tracking-tight text-white/85 leading-snug">
          We do not sell technology for technology's sake.<br />
          <span className="text-white/45">We design systems around measurable business outcomes.</span>
        </p>
      </FadeUp>

      <div className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-10 border-t border-white/8 pt-14" data-testid="stats-counters">
        {STATS.map((s) => (
          <Counter key={s.label} {...s} />
        ))}
      </div>
    </div>
  </section>
);
